import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Plus, MagnifyingGlass, Trash, DownloadSimple, FilePdf, ArrowUp, ArrowDown, Upload, FloppyDisk, Check, Package } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import { useAuth, isAdmin } from "@/lib/access";

applyPlugin(jsPDF);

const API = `${window.location.origin}/api`;
const DEFAULT_CATEGORIES = ["Spices", "Millet", "Flour", "Masala", "Herbs", "Dry Fruits", "Others"];
const UNITS = ["kg", "g", "pcs"];

const formatCurrency = (v) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(v);
const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) + " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

const EDITABLE_COLS = ["product_name", "hsn_code", "category", "variant", "price", "unit", "quantity"];

const EMPTY_ROW = { product_name: "", hsn_code: "", category: "Spices", variant: "", price: "", unit: "kg", quantity: "1", notes: "" };

export default function ProductRatesPage() {
  const user = useAuth();
  const admin = isAdmin(user);
  const canEditPrice = admin || !!user?.can_edit_rates;
  const canEditCol = (col) => admin || (canEditPrice && col === "price");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterVariant, setFilterVariant] = useState("");
  const [sortBy, setSortBy] = useState("last_updated");
  const [sortOrder, setSortOrder] = useState("desc");
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [variants, setVariants] = useState([]);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyName, setCompanyName] = useState("Huma Spices");
  const [showLogoDialog, setShowLogoDialog] = useState(false);

  // Inline editing state
  const [editRows, setEditRows] = useState({});
  const [dirtyRows, setDirtyRows] = useState(new Set());
  const [savingRows, setSavingRows] = useState(new Set());
  const [newRows, setNewRows] = useState([]);
  const cellRefs = useRef({});

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCategory && filterCategory !== "all") params.category = filterCategory;
      if (filterVariant && filterVariant !== "all") params.variant = filterVariant;
      if (search) params.search = search;
      if (sortBy) { params.sort_by = sortBy; params.sort_order = sortOrder; }
      const res = await axios.get(`${API}/products`, { params });
      setProducts(res.data);
      const rows = {};
      res.data.forEach(p => {
        rows[p.product_id] = {
          product_name: p.product_name, hsn_code: p.hsn_code || "", category: p.category, variant: p.variant || "",
          price: String(p.price), unit: p.unit || "kg", quantity: String(p.quantity || 1), notes: p.notes || ""
        };
      });
      setEditRows(rows);
      setDirtyRows(new Set());
    } catch (err) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterVariant, search, sortBy, sortOrder]);

  const fetchMeta = useCallback(async () => {
    try {
      const [catRes, varRes, brandRes] = await Promise.all([
        axios.get(`${API}/products-categories/list`),
        axios.get(`${API}/products-variants/list`),
        axios.get(`${API}/company/branding`)
      ]);
      setCategories(catRes.data.length > 0 ? catRes.data : DEFAULT_CATEGORIES);
      setVariants(varRes.data);
      if (brandRes.data.logo) setCompanyLogo(brandRes.data.logo);
      if (brandRes.data.company_name) setCompanyName(brandRes.data.company_name);
    } catch (err) { toast.error("Failed to load product metadata"); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  // --- Inline Edit Handlers ---
  const updateCell = (productId, field, value) => {
    setEditRows(prev => ({ ...prev, [productId]: { ...prev[productId], [field]: value } }));
    setDirtyRows(prev => new Set(prev).add(productId));
  };

  const getRowTotal = (row) => {
    const p = parseFloat(row.price) || 0;
    const q = parseFloat(row.quantity) || 1;
    return (p * q).toFixed(2);
  };

  const saveRow = async (productId) => {
    const row = editRows[productId];
    if (!row || !row.product_name || !row.price) return;
    try {
      setSavingRows(prev => new Set(prev).add(productId));
      await axios.put(`${API}/products/${productId}`, {
        ...row, price: parseFloat(row.price), quantity: parseFloat(row.quantity) || 1
      });
      setDirtyRows(prev => { const n = new Set(prev); n.delete(productId); return n; });
      // Refresh the product's last_updated from server
      const res = await axios.get(`${API}/products/${productId}`);
      setProducts(prev => prev.map(p => p.product_id === productId ? res.data : p));
    } catch (err) {
      toast.error(`Failed to save ${row.product_name}`);
    } finally {
      setSavingRows(prev => { const n = new Set(prev); n.delete(productId); return n; });
    }
  };

  const saveAllDirty = async () => {
    const ids = [...dirtyRows];
    if (ids.length === 0) { toast.info("No changes to save"); return; }
    let saved = 0;
    for (const id of ids) {
      try {
        const row = editRows[id];
        if (!row.product_name || !row.price) continue;
        await axios.put(`${API}/products/${id}`, {
          ...row, price: parseFloat(row.price), quantity: parseFloat(row.quantity) || 1
        });
        saved++;
      } catch (err) { toast.error(`Save failed for ${id}`); }
    }
    toast.success(`${saved} product${saved !== 1 ? "s" : ""} saved!`);
    fetchProducts();
    fetchMeta();
  };

  // --- New Row Handlers ---
  const addNewRow = () => {
    setNewRows(prev => [...prev, { ...EMPTY_ROW, _tempId: `new-${Date.now()}-${prev.length}` }]);
  };

  const updateNewRow = (tempId, field, value) => {
    setNewRows(prev => prev.map(r => r._tempId === tempId ? { ...r, [field]: value } : r));
  };

  const saveNewRow = async (tempId) => {
    const row = newRows.find(r => r._tempId === tempId);
    if (!row || !row.product_name || !row.price) {
      toast.error("Product name and price are required");
      return;
    }
    try {
      await axios.post(`${API}/products`, {
        ...row, price: parseFloat(row.price), quantity: parseFloat(row.quantity) || 1
      });
      toast.success(`${row.product_name} added!`);
      setNewRows(prev => prev.filter(r => r._tempId !== tempId));
      fetchProducts();
      fetchMeta();
    } catch (err) {
      toast.error("Failed to add product");
    }
  };

  const removeNewRow = (tempId) => {
    setNewRows(prev => prev.filter(r => r._tempId !== tempId));
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.product_name}"?`)) return;
    try {
      await axios.delete(`${API}/products/${p.product_id}`);
      toast.success("Product deleted");
      fetchProducts();
    } catch (err) { toast.error("Failed to delete"); }
  };

  // --- Keyboard Navigation ---
  const focusCell = (rowId, colIdx) => {
    const ref = cellRefs.current[`${rowId}-${colIdx}`];
    if (ref) ref.focus();
  };

  const handleKeyDown = (e, rowId, colIdx, allRowIds) => {
    if (e.key === "Tab") {
      e.preventDefault();
      const nextCol = e.shiftKey ? colIdx - 1 : colIdx + 1;
      if (nextCol >= 0 && nextCol < EDITABLE_COLS.length) {
        focusCell(rowId, nextCol);
      } else if (!e.shiftKey && nextCol >= EDITABLE_COLS.length) {
        const rowIdx = allRowIds.indexOf(rowId);
        if (rowIdx < allRowIds.length - 1) focusCell(allRowIds[rowIdx + 1], 0);
      } else if (e.shiftKey && nextCol < 0) {
        const rowIdx = allRowIds.indexOf(rowId);
        if (rowIdx > 0) focusCell(allRowIds[rowIdx - 1], EDITABLE_COLS.length - 1);
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      const rowIdx = allRowIds.indexOf(rowId);
      if (rowIdx < allRowIds.length - 1) focusCell(allRowIds[rowIdx + 1], colIdx);
    }
  };

  // --- Sort ---
  const toggleSort = (field) => {
    if (sortBy === field) setSortOrder(prev => prev === "desc" ? "asc" : "desc");
    else { setSortBy(field); setSortOrder("desc"); }
  };
  const SortIcon = ({ field }) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? <ArrowUp size={12} weight="bold" /> : <ArrowDown size={12} weight="bold" />;
  };

  // --- Logo ---
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await axios.post(`${API}/company/logo`, { logo: reader.result, company_name: companyName });
        setCompanyLogo(reader.result);
        toast.success("Logo uploaded!");
        setShowLogoDialog(false);
      } catch (err) { toast.error("Failed to upload logo"); }
    };
    reader.readAsDataURL(file);
  };

  // --- PDF ---
  const downloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 15;
    if (companyLogo) {
      try { doc.addImage(companyLogo, "PNG", 14, y, 25, 25); } catch (err) { toast.error("Logo failed to render in PDF"); }
      doc.setFontSize(22); doc.setFont("helvetica", "bold"); doc.text(companyName, 44, y + 12);
      doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.text("Daily Product Rate List", 44, y + 20);
      y += 32;
    } else {
      doc.setFontSize(22); doc.setFont("helvetica", "bold"); doc.text(companyName, 14, y + 8);
      doc.setFontSize(10); doc.setFont("helvetica", "normal"); doc.text("Daily Product Rate List", 14, y + 16);
      y += 24;
    }
    doc.setDrawColor(99, 102, 241); doc.setLineWidth(0.5); doc.line(14, y, pageWidth - 14, y); y += 4;
    doc.setFontSize(9); doc.setTextColor(100);
    const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
    doc.text(`Date: ${today}`, 14, y + 4);
    doc.text(`Total Products: ${products.length}`, pageWidth - 14, y + 4, { align: "right" }); y += 10;
    doc.autoTable({
      startY: y,
      head: [["Product Name", "HSN Code", "Category", "Variant", "Price", "Unit", "Qty", "Total"]],
      body: products.map(p => [p.product_name, p.hsn_code || "-", p.category, p.variant || "-", formatCurrency(p.price), p.unit || "kg", String(p.quantity), formatCurrency(p.total_price)]),
      theme: "grid",
      headStyles: { fillColor: [99, 102, 241], textColor: 255, fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 4: { halign: "right" }, 5: { halign: "center" }, 6: { halign: "center" }, 7: { halign: "right", fontStyle: "bold" } },
      alternateRowStyles: { fillColor: [248, 249, 250] }
    });
    doc.setFontSize(8); doc.setTextColor(150);
    doc.text(`Generated by ${companyName} on ${today}`, pageWidth / 2, doc.lastAutoTable.finalY + 10, { align: "center" });
    doc.save(`${companyName.replace(/\s/g, "_")}_Product_Rates_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF downloaded!");
  };

  // Build all row IDs for keyboard navigation
  const allRowIds = [...products.map(p => p.product_id), ...newRows.map(r => r._tempId)];

  // Render an editable cell
  const renderCell = (rowId, col, value, onChange, isNew = false) => {
    const key = `${rowId}-${EDITABLE_COLS.indexOf(col)}`;
    const colIdx = EDITABLE_COLS.indexOf(col);
    const isNumeric = col === "price" || col === "quantity";

    if (!isNew && !canEditCol(col)) {
      return (
        <span className={`block px-2 py-1.5 text-sm text-gray-800 ${isNumeric ? "text-right font-mono" : ""} ${col === "product_name" ? "font-semibold" : ""}`} data-testid={`cell-${col}-${rowId}`}>
          {value || "-"}
        </span>
      );
    }

    if (col === "category") {
      return (
        <select
          ref={el => { cellRefs.current[key] = el; }}
          value={value}
          onChange={(e) => onChange(col, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, rowId, colIdx, allRowIds)}
          className="h-8 w-full bg-transparent border border-transparent hover:border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded px-1 text-sm outline-none"
          data-testid={`cell-${col}-${rowId}`}
        >
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      );
    }

    if (col === "unit") {
      return (
        <select
          ref={el => { cellRefs.current[key] = el; }}
          value={value}
          onChange={(e) => onChange(col, e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, rowId, colIdx, allRowIds)}
          className="h-8 w-full bg-transparent border border-transparent hover:border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded px-1 text-sm outline-none"
          data-testid={`cell-${col}-${rowId}`}
        >
          {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      );
    }

    return (
      <input
        ref={el => { cellRefs.current[key] = el; }}
        type={isNumeric ? "number" : "text"}
        step={isNumeric ? "0.01" : undefined}
        value={value}
        onChange={(e) => onChange(col, e.target.value)}
        onBlur={() => { if (!isNew && dirtyRows.has(rowId)) saveRow(rowId); }}
        onKeyDown={(e) => handleKeyDown(e, rowId, colIdx, allRowIds)}
        placeholder={col === "product_name" ? "Product name..." : col === "variant" ? "Variant..." : col === "price" ? "0.00" : "1"}
        className={`h-8 w-full bg-transparent border border-transparent hover:border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 rounded px-2 text-sm outline-none ${isNumeric ? "text-right font-mono" : ""} ${col === "product_name" ? "font-semibold" : ""}`}
        data-testid={`cell-${col}-${rowId}`}
      />
    );
  };

  const dirtyCount = dirtyRows.size;

  return (
    <div className="space-y-4 max-w-full" data-testid="product-rates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Daily Product Rates</h1>
          <p className="text-sm text-gray-500" data-testid="product-rates-subtitle">{products.length} products | {admin ? "Click any cell to edit inline" : canEditPrice ? "Click a price to update it — changes auto-save" : "View-only rate list"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {dirtyCount > 0 && (
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white animate-pulse" onClick={saveAllDirty} data-testid="save-all-btn">
              <FloppyDisk size={16} className="mr-1" /> Save All ({dirtyCount})
            </Button>
          )}
          {admin && (
          <Button variant="outline" size="sm" onClick={() => setShowLogoDialog(true)} data-testid="upload-logo-btn">
            <Upload size={16} className="mr-1" /> Logo
          </Button>
          )}
          <Button variant="outline" size="sm" onClick={downloadPDF} disabled={products.length === 0} data-testid="download-pdf-btn">
            <FilePdf size={16} className="mr-1" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.open(`${API}/export/products`, "_blank")} data-testid="export-csv-btn">
            <DownloadSimple size={16} className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      {/* Company Logo Preview */}
      {companyLogo && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 px-4 py-2 rounded-lg">
          <img src={companyLogo} alt="Logo" className="h-8 w-8 object-contain rounded" />
          <span className="text-sm font-semibold text-indigo-900">{companyName}</span>
          <span className="text-xs text-indigo-600">Logo will appear in PDF exports</span>
        </div>
      )}

      {/* Filters */}
      <Card className="p-3 border border-gray-200 bg-white">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="product-search" />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[160px] h-9" data-testid="filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterVariant} onValueChange={setFilterVariant}>
            <SelectTrigger className="w-[160px] h-9" data-testid="filter-variant"><SelectValue placeholder="All Variants" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Variants</SelectItem>
              {variants.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setFilterCategory(""); setFilterVariant(""); }} data-testid="clear-filters-btn">Clear</Button>
        </div>
      </Card>

      {/* Spreadsheet Table */}
      <Card className="border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="products-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[18%] cursor-pointer select-none" onClick={() => toggleSort("product_name")} data-testid="sort-name">
                  <span className="flex items-center gap-1">Product Name <SortIcon field="product_name" /></span>
                </th>
                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[9%]">HSN Code</th>
                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[12%] cursor-pointer select-none" onClick={() => toggleSort("category")} data-testid="sort-category">
                  <span className="flex items-center gap-1">Category <SortIcon field="category" /></span>
                </th>
                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[11%]">Variant</th>
                <th className="px-2 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-[10%] cursor-pointer select-none" onClick={() => toggleSort("price")} data-testid="sort-price">
                  <span className="flex items-center justify-end gap-1">Price <SortIcon field="price" /></span>
                </th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-[8%]">Unit</th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-[7%]">Qty</th>
                <th className="px-2 py-2.5 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-[10%]">Total</th>
                <th className="px-2 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[10%] cursor-pointer select-none" onClick={() => toggleSort("last_updated")} data-testid="sort-updated">
                  <span className="flex items-center gap-1">Updated <SortIcon field="last_updated" /></span>
                </th>
                <th className="px-2 py-2.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-[5%]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400">Loading...</td></tr>
              ) : products.length === 0 && newRows.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400">No products yet. Click "+ Add New Row" below to start!</td></tr>
              ) : (
                <>
                  {/* Existing product rows (inline editable) */}
                  {products.map((p) => {
                    const row = editRows[p.product_id] || {};
                    const total = getRowTotal(row);
                    const isDirty = dirtyRows.has(p.product_id);
                    const isSaving = savingRows.has(p.product_id);

                    return (
                      <tr key={p.product_id} className={`transition-colors ${isDirty ? "bg-amber-50" : "hover:bg-gray-50"} ${isSaving ? "opacity-60" : ""}`} data-testid={`product-row-${p.product_id}`}>
                        <td className="px-1 py-1">
                          {renderCell(p.product_id, "product_name", row.product_name || "", (f, v) => updateCell(p.product_id, f, v))}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(p.product_id, "hsn_code", row.hsn_code || "", (f, v) => updateCell(p.product_id, f, v))}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(p.product_id, "category", row.category || "Spices", (f, v) => updateCell(p.product_id, f, v))}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(p.product_id, "variant", row.variant || "", (f, v) => updateCell(p.product_id, f, v))}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(p.product_id, "price", row.price || "", (f, v) => updateCell(p.product_id, f, v))}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(p.product_id, "unit", row.unit || "kg", (f, v) => updateCell(p.product_id, f, v))}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(p.product_id, "quantity", row.quantity || "1", (f, v) => updateCell(p.product_id, f, v))}
                        </td>
                        <td className="px-2 py-1 text-right">
                          <span className="text-sm font-bold text-green-700" data-testid={`total-${p.product_id}`}>{formatCurrency(parseFloat(total))}</span>
                        </td>
                        <td className="px-2 py-1 text-xs text-gray-400">{formatDate(p.last_updated)}</td>
                        <td className="px-1 py-1 text-center">
                          <div className="flex items-center gap-0.5">
                            {isDirty && (
                              <button onClick={() => saveRow(p.product_id)} className="p-1 rounded hover:bg-green-100 text-green-600" title="Save" data-testid={`save-row-${p.product_id}`}>
                                <Check size={14} weight="bold" />
                              </button>
                            )}
                            {admin && (
                            <button onClick={() => handleDelete(p)} className="p-1 rounded hover:bg-red-100 text-red-400 hover:text-red-600" title="Delete" data-testid={`delete-product-${p.product_id}`}>
                              <Trash size={14} />
                            </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {/* New product rows (not yet saved) */}
                  {newRows.map((nr) => {
                    const total = getRowTotal(nr);
                    return (
                      <tr key={nr._tempId} className="bg-green-50 border-l-2 border-l-green-400" data-testid={`new-row-${nr._tempId}`}>
                        <td className="px-1 py-1">
                          {renderCell(nr._tempId, "product_name", nr.product_name, (f, v) => updateNewRow(nr._tempId, f, v), true)}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(nr._tempId, "hsn_code", nr.hsn_code || "", (f, v) => updateNewRow(nr._tempId, f, v), true)}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(nr._tempId, "category", nr.category, (f, v) => updateNewRow(nr._tempId, f, v), true)}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(nr._tempId, "variant", nr.variant, (f, v) => updateNewRow(nr._tempId, f, v), true)}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(nr._tempId, "price", nr.price, (f, v) => updateNewRow(nr._tempId, f, v), true)}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(nr._tempId, "unit", nr.unit, (f, v) => updateNewRow(nr._tempId, f, v), true)}
                        </td>
                        <td className="px-1 py-1">
                          {renderCell(nr._tempId, "quantity", nr.quantity, (f, v) => updateNewRow(nr._tempId, f, v), true)}
                        </td>
                        <td className="px-2 py-1 text-right">
                          <span className="text-sm font-bold text-green-700">{formatCurrency(parseFloat(total))}</span>
                        </td>
                        <td className="px-2 py-1 text-xs text-gray-400 italic">New</td>
                        <td className="px-1 py-1 text-center">
                          <div className="flex items-center gap-0.5">
                            <button onClick={() => saveNewRow(nr._tempId)} className="p-1 rounded hover:bg-green-100 text-green-600" title="Save new product" data-testid={`save-new-${nr._tempId}`}>
                              <Check size={14} weight="bold" />
                            </button>
                            <button onClick={() => removeNewRow(nr._tempId)} className="p-1 rounded hover:bg-red-100 text-red-400" title="Discard" data-testid={`discard-new-${nr._tempId}`}>
                              <Trash size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Quick Add Row button */}
        {admin && (
        <div className="border-t border-gray-200 p-2">
          <button
            onClick={addNewRow}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-indigo-600 hover:bg-indigo-50 rounded transition-colors font-medium"
            data-testid="add-new-row-btn"
          >
            <Plus size={16} weight="bold" /> Add New Product Row
          </button>
        </div>
        )}
      </Card>

      {/* Keyboard hint */}
      {admin && (
      <div className="flex items-center gap-4 text-xs text-gray-400">
        <span><kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">Tab</kbd> next field</span>
        <span><kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">Enter</kbd> next row</span>
        <span><kbd className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">Shift+Tab</kbd> previous field</span>
        <span>Changes auto-save on blur</span>
      </div>
      )}

      {/* Logo Upload Dialog */}
      <Dialog open={showLogoDialog} onOpenChange={setShowLogoDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Gupter', serif" }}>Company Logo & Branding</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Company Name</label>
              <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Company name for PDF header" data-testid="company-name-input" />
            </div>
            {companyLogo && (
              <div className="flex items-center justify-center p-4 bg-gray-50 border rounded-lg">
                <img src={companyLogo} alt="Current logo" className="max-h-20 object-contain" />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Upload Logo (PNG/JPG, max 2MB)</label>
              <Input type="file" accept="image/png,image/jpeg,image/jpg" onChange={handleLogoUpload} data-testid="logo-file-input" />
            </div>
            <p className="text-xs text-gray-500">The logo will appear in the top-left corner of your PDF exports.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
